import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { Org } from '../../../enum';
import { APIStatus } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { type FilteredInfo, type RequestParams, SortedInfo, type TableChange } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

export class CohortDetails {
  total_count: number = 0;
  id: string | undefined = undefined;
  name: string | undefined = undefined;
  org_code: Org | null = null;
  org_name: string | undefined = undefined;
  org_timezone_name: string | null = null;
  created: string | undefined = undefined;

  unlock_codes: string[] = [];
  advocates: { name: string; id: string }[] = [];

  advocate_count: number = 0;
}

export const COHORT_MODAL = {
  DETAILS: 'details',
  IDLE: 'idle',
  EDIT_COHORT: 'edit_cohort',
} as const;

export type COHORT_MODAL = (typeof COHORT_MODAL)[keyof typeof COHORT_MODAL];

type AdminCohortSliceType = {
  cohorts: CohortDetails[];
  requestAdminCohortParam: RequestParams<CohortDetails>;
  adminCohortApiStatus: APIStatus;
  cohortDetailsModal: COHORT_MODAL;
  cohortDetails: CohortDetails;
};

const initialState: AdminCohortSliceType = {
  cohorts: [],
  requestAdminCohortParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<CohortDetails>('name', 'ascend'),
    filteredInfo: {},
  },
  adminCohortApiStatus: APIStatus.IDLE,
  cohortDetailsModal: COHORT_MODAL.IDLE,
  cohortDetails: new CohortDetails(),
};

export const fetchCohorts = createAsyncThunk<CohortDetails[], undefined, AsyncThunkConfig>(
  'adminCohort/fetchCohorts',
  async (_, thunkAPI) => {
    try {
      const requestAdminCohortParam = thunkAPI.getState().adminCohortSlice.requestAdminCohortParam;
      const response = (await axios.post('v0_cohorts', requestAdminCohortParam)) as CohortDetails[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminCohortSlice', 'fetchCohorts']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export type SetAdminCohortStatusData = {
  manager_id: string;
  status: string;
};

export const adminCohortSlice = createSlice({
  name: 'adminCohort',
  initialState,
  reducers: {
    handleManagerTableChange: (state, action: PayloadAction<TableChange<CohortDetails>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<CohortDetails>;
      state.requestAdminCohortParam.pagination = action.payload.pagination;
      state.requestAdminCohortParam.sortedInfo = sorter;
      state.requestAdminCohortParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestAdminCohortParam.pagination = initialState.requestAdminCohortParam.pagination;
      state.requestAdminCohortParam.sortedInfo = initialState.requestAdminCohortParam.sortedInfo;
      state.requestAdminCohortParam.filteredInfo = initialState.requestAdminCohortParam.filteredInfo;
    },
    showModal: (state, action: PayloadAction<COHORT_MODAL>) => {
      state.cohortDetailsModal = action.payload;
    },
    setCohortsDetails: (state, action: PayloadAction<CohortDetails>) => {
      state.cohortDetails = action.payload;
    },
    closeCohortDetailsModal: (state) => {
      state.cohortDetailsModal = COHORT_MODAL.IDLE;
      state.cohortDetails = new CohortDetails();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCohorts.pending, (state, _action) => {
        state.adminCohortApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchCohorts.fulfilled, (state, action) => {
        state.cohorts = action.payload;
        state.adminCohortApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchCohorts.rejected, (state, _action) => {
        state.adminCohortApiStatus = APIStatus.ERROR;
      });
  },
});

export const { handleManagerTableChange, clearFilter, showModal, setCohortsDetails, closeCohortDetailsModal } =
  adminCohortSlice.actions;
